import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { fetchLabels } from '../utils/labelApi';
import './Inbox.css';

const parseSenderDisplay = (from) => {
  const raw = String(from || '').trim();
  if (!raw) return 'Unknown';
  const match = raw.match(/^(?:"([^"]+)"|([^<]+?))\s*<([^>]+)>$/);
  if (match) {
    const name = (match[1] || match[2] || '').trim();
    if (name) return name;
    return match[3].split('@')[0];
  }
  if (raw.includes('@')) {
    const name = raw.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/[._-]/g, ' ');
  }
  return raw;
};

const isImapMail = (m) => m && (m.source === 'imap' || m.uid);
const getMailUid = (mail) => mail?.uid ?? mail?.imapUid;
const getMailKey = (mail) => String(getMailUid(mail) ?? mail?._id ?? mail?.messageId ?? '');
const isMailRead = (mail) => Boolean(mail?.seen ?? mail?.isRead);

const getMailPreview = (mail) => {
  if (mail?.preview) return mail.preview;
  let raw = mail?.body || mail?.text || '';
  if (typeof raw === 'string') {
    raw = raw.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }
  return raw.length > 100 ? `${raw.substring(0, 100)}...` : raw;
};

const SearchResults = ({ currentUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 768);
  const abortRef = useRef(null);
  const isSplitPane = !isMobileView;

  const parseLabelQuery = (raw) => {
    const match = String(raw || '').trim().match(/^label:\s*(.+)$/i);
    return match ? match[1].trim().replace(/^["']|["']$/g, '') : '';
  };

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const searchMails = useCallback(async () => {
    const q = query.trim();
    if (!q) {
      setMails([]);
      setLoading(false);
      setError('');
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setLoading(true);
    setError('');

    try {
      const labelName = parseLabelQuery(q);
      if (labelName) {
        const labelsResponse = await fetchLabels(true);
        const match = (labelsResponse.data || []).find(
          (label) => String(label.name).toLowerCase() === labelName.toLowerCase()
        );
        if (!match) {
          setError(`No label named "${labelName}"`);
          setMails([]);
          return;
        }
        navigate(`/labels/${match._id}`, { replace: true });
        return;
      }

      const response = await axiosInstance.get(
        `/api/mail/server/search?q=${encodeURIComponent(q)}`,
        { signal: abortRef.current.signal }
      );
      if (response.data.success) {
        setMails(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to search mails');
        setMails([]);
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      const code = err.response?.data?.code;
      if (code === 'MAILBOX_CREDENTIALS_REQUIRED' || code === 'MAILBOX_AUTH_FAILED') return;
      console.error('Error searching mails:', err);
      setError(err.response?.data?.message || 'Error searching mails. Please try again.');
      setMails([]);
    } finally {
      setLoading(false);
    }
  }, [query, navigate]);

  useEffect(() => {
    if (!currentUser) return;
    setSelectedMail(null);
    if (!query.trim()) {
      setMails([]);
      setLoading(false);
      return;
    }
    searchMails();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [currentUser, query, searchMails]);

  const handleMailClick = async (mail) => {
    if (isImapMail(mail)) {
      const uid = getMailUid(mail);
      setSelectedMail({
        ...mail,
        body: mail.body || mail.preview || '',
        text: mail.text || mail.preview || '',
      });

      if (!uid) return;

      try {
        const detail = await axiosInstance.get(`/api/mail/server/message/${uid}`);
        if (detail.data.success) {
          const m = detail.data.data;
          setSelectedMail(m);
          if (!isMailRead(m)) {
            try {
              await axiosInstance.patch(`/api/mail/server/read/${uid}`);
              setMails((prev) =>
                prev.map((x) => (getMailUid(x) === uid ? { ...x, seen: true, isRead: true } : x))
              );
            } catch (_e) { /* best-effort */ }
          }
        }
      } catch (err) {
        console.error('Error loading message:', err);
      }
      return;
    }

    setSelectedMail(mail);
    if (!isMailRead(mail) && mail._id) {
      try {
        await axiosInstance.patch(`/api/mail/mark-read/${mail._id}`);
        setMails((prev) => prev.map((m) => (m._id === mail._id ? { ...m, isRead: true } : m)));
      } catch (_e) { /* ignore */ }
    }
  };

  const handleCloseMail = () => setSelectedMail(null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!query.trim()) {
    return (
      <div className="inbox-container">
        <div className="empty-inbox">
          <p>Type in the search bar to find mail</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inbox-container">
      {/* <div className="inbox-header search-results-header">
        <h2>Search results</h2>
        <p className="mail-count">
          {loading ? 'Searching...' : `${mails.length} ${mails.length === 1 ? 'result' : 'results'} for "${query}"`}
        </p>
      </div> */}
      {error && (
        <div className="search-error-bar">
          <span>{error}</span>
          <button type="button" onClick={searchMails} className="retry-button">Retry</button>
        </div>
      )}
      <div className={`inbox-content ${isSplitPane ? 'gmail-split-layout' : ''} ${selectedMail && isSplitPane ? 'has-detail-open' : ''}`}>
        <div className={`mail-list-container ${selectedMail && !isSplitPane ? 'hidden' : ''}`}>
          <div className="mail-list">
            {!loading && mails.length === 0 && !error ? (
              <div className="empty-inbox">
                <p>No results for &quot;{query}&quot;</p>
                <p className="empty-hint">Try different keywords</p>
              </div>
            ) : (
              mails.map((mail) => {
                const mailKey = getMailKey(mail);
                const preview = getMailPreview(mail);
                return (
                  <div
                    key={mailKey}
                    role="button"
                    tabIndex={0}
                    className={`mail-item gmail-split-row ${!isMailRead(mail) ? 'unread' : 'read'} ${selectedMail && getMailKey(selectedMail) === mailKey ? 'selected' : ''}`}
                    onClick={() => handleMailClick(mail)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleMailClick(mail);
                      }
                    }}
                  >
                    <span className={`mail-read-dot ${!isMailRead(mail) ? 'visible' : ''}`} aria-hidden="true" />
                    <div className="gmail-row-body">
                      <div className="gmail-row-top">
                        <span className="mail-sender">{parseSenderDisplay(mail.from)}</span>
                        <span className="mail-date">{formatDate(mail.createdAt || mail.date)}</span>
                      </div>
                      <div className="gmail-row-subject">{mail.subject || '(no subject)'}</div>
                      {preview ? <span className="gmail-row-snippet">{preview}</span> : null}
                    </div>
                  </div>
                );
              })
            )}
            {loading && (
              <div className="search-loading-inline">
                <span>Searching your mailbox...</span>
              </div>
            )}
          </div>
        </div>
        {selectedMail && (
          <div className="mail-detail-container">
            <div className="mail-detail-header">
              <button type="button" onClick={handleCloseMail} className="back-button" aria-label="Back">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                </svg>
              </button>
              <div className="header-subject-section">
                <h3 className="header-subject">{selectedMail.subject || '(No subject)'}</h3>
              </div>
            </div>
            <div className="mail-detail-content">
              <div className="mail-detail-row">
                <div className="detail-label">From</div>
                <div className="detail-value">{selectedMail.from}</div>
              </div>
              <div className="mail-detail-row">
                <div className="detail-label">To</div>
                <div className="detail-value">{selectedMail.to}</div>
              </div>
              <div className="mail-detail-row">
                <div className="detail-label">Date</div>
                <div className="detail-value">{formatFullDate(selectedMail.createdAt || selectedMail.date)}</div>
              </div>
              <div className="mail-body">
                <div className="body-content" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedMail.body || selectedMail.text || selectedMail.preview || ''}
                </div>
              </div>
            </div>
          </div>
        )}
        {!selectedMail && isSplitPane && mails.length > 0 && (
          <div className="mail-detail-container empty-detail-pane">
            <div className="empty-detail-content">
              <p>Select a message to read</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;

